<template>
  <div :style="{ backgroundColor: item.custom_properties.hex }" style="aspect-ratio: 1/1;"
    class="flex flex-col items-center border delay-750 duration-150 transition-all p-[6px] hover:p-[0px] rounded-[8px] shadow w-full relative group overflow-hidden">

    <div v-if="typeItem === 'image'"
      class="hidden group-hover:block absolute bg-black bg-opacity-25 left-[8px] px-[10px] py-[2px] rounded-[3px] text-[10px] text-white top-[8px] z-50">
      {{ item.mime_type === 'image/gif' ? 'GIF' : 'Image' }}
    </div>

    <div v-else-if="typeItem === 'video'"
      class="hidden group-hover:block absolute bg-black bg-opacity-25 left-[8px] px-[10px] py-[2px] rounded-[3px] text-[10px] text-white top-[8px] z-50">
      Video
    </div>

    <div v-else-if="typeItem === 'vimeo'"
      class="hidden group-hover:block absolute bg-black bg-opacity-25 left-[8px] px-[10px] py-[2px] rounded-[3px] text-[10px] text-white top-[8px] z-50">
      Vimeo
    </div>

    <div v-if="item.preview_url" @click="showInfo">
      <div v-if="item.mime_type === 'image/gif'" class="absolute inset-0 bg-cover bg-center z-40 pointer-events-none 
        transition-all duration-500 transform origin-center 
        opacity-0 group-hover:opacity-100
        scale-90 group-hover:scale-100" :style="{ backgroundImage: 'url(' + item.original_url + ')' }">
      </div>
      <img :src="item.preview_url" alt="Preview" loading="lazy"
        class="w-full h-auto rounded transition-opacity duration-500"
        :class="{ 'opacity-0': !imageLoaded, 'opacity-100': imageLoaded }" @load="imageLoaded = true" />
    </div>

    <JetDialogModal :show="isModalVisible" @close="isModalVisible = false">
      <template #title>Info media</template>
      <template #content>

        <div>
          <div v-if="isLoading" class="text-sm text-gray-500">Loading...</div>
          <div v-else-if="error" class="text-red-500 text-sm">{{ error }}</div>
          <template v-else-if="modelData">

            <div v-if="modelData.extra"
              class="flex flex-col gap-[10px] bg-slate-50 mb-[20px] p-[10px] md:p-[20px] rounded-[4px]">

              <div class="flex flex-row gap-[10px]">

                <div v-if="modelData.extra.image"
                  class="bg-slate-100 overflow-hidden rounded-full size-[50px] shrink-0">
                  <img v-if="modelData.extra.image.match(/\.(jpeg|jpg|gif|png)$/i)" :src="modelData.extra.image"
                    class="w-full h-full object-cover" />
                  <img v-if="modelData.extra.image.match(/\.(mp4|webm|ogg)$/i)" :src="modelData.extra.image"
                    class="w-full h-full object-cover" />
                  <!-- <video v-else-if="modelData.extra.image.match(/\.(mp4|webm|ogg)$/i)" :src="modelData.extra.image"
                    class="w-full h-full object-cover" autoplay loop muted></video> -->
                </div>

                <div class="flex flex-col">
                  <div class="text-slate-600 font-bold">{{ modelData.extra.type }}</div>
                  <a :href="modelData.extra.link" class="text-sky-500 hover:text-sky-800" target="_blank"
                    v-html="modelData.extra.name"></a>
                </div>
              </div>

              <a v-if="typeItem === 'image'" :href="item.original_url" target="_blank">
                <JetSecondaryButton class="w-full md:w-fit text-center">Download image</JetSecondaryButton>
              </a>

              <a v-if="typeItem === 'video'" :href="item.original_url" target="_blank">
                <JetSecondaryButton class="w-full md:w-fit text-center">Download video</JetSecondaryButton>
              </a>

              <a v-if="typeItem === 'vimeo'" :href="modelData.extra.vimeo_url" target="_blank">
                <JetSecondaryButton class="w-full md:w-fit text-center">Show vimeo video</JetSecondaryButton>
              </a>

            </div>

            <pre v-else>{{ modelData }}</pre>

          </template>
          <div v-else class="text-sm text-gray-500">No information available</div>

        </div>

        <div v-if="typeItem === 'image'" class="overflow-hidden rounded-[4px] w-full">
          <img :src="item.original_url" alt="Full Image" class="w-full h-auto rounded" />
        </div>
        <div v-else-if="typeItem === 'video'" class="overflow-hidden rounded-[4px] w-full">
          <video :src="item.original_url" controls class="w-full h-auto rounded"></video>
        </div>
        <div v-else-if="typeItem === 'vimeo' && modelData.extra.vimeo_url" class="overflow-hidden rounded-[4px] w-full">

          <div class="relative pb-[56.25%]">
            <iframe :src="modelData.extra.vimeo_url" class="absolute top-0 left-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture" allowfullscreen>
            </iframe>
          </div>

        </div>

      </template>

      <template #footer>
        <JetSecondaryButton @click="isModalVisible = false">Close</JetSecondaryButton>
      </template>
    </JetDialogModal>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetButton from '@/Jetstream/Button.vue';

const props = defineProps({
  item: Object,
});

const predefinedModels = [
  "App\\Models\\WorkItemAtom",
  "App\\Models\\Work",
  "App\\Models\\TeamMember",
  "App\\Models\\Publication",
  "App\\Models\\Event",
  "App\\Models\\Newsletter",
  "App\\Models\\BundleItem",
  "App\\Models\\Setting",
  "App\\Models\\News",
  "App\\Models\\Award",
  "App\\Models\\Page",
  "App\\Models\\PressKitItem",
  "App\\Models\\VacancyItem",
];

const isModalVisible = ref(false);
const imageLoaded = ref(false);
const modelData = ref(null);
const isLoading = ref(false);
const error = ref(null);

watch(isModalVisible, (newVal) => {
  if (!newVal) {
    modelData.value = null;
    error.value = null;
  }
});

const fetchModelInfo = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await axios.get('/admin/view-media/info-model', {
      params: {
        model_type: props.item.model_type,
        model_id: props.item.model_id
      }
    });

    if (response.data.success) {
      modelData.value = response.data.data;
    } else {
      throw new Error(response.data.message || 'Invalid response format');
    }
  } catch (err) {
    error.value = err.response?.data?.message ||
      err.message ||
      'Failed to fetch model information';
    console.error('API Error:', err);
  } finally {
    isLoading.value = false;
  }
};

const showInfo = async () => {
  isModalVisible.value = true;
  if (!modelData.value && !isLoading.value) {
    await fetchModelInfo();
  }
};

const typeItem = computed(() => {

  let result

  if (props.item.mime_type.startsWith('image/')) {
    result = 'image'
  }

  if (props.item.mime_type.startsWith('video/')) {
    result = 'video'
  }

  if (props.item.model_type === 'App\\Models\\VimeoMedia') {
    result = 'vimeo'
  }

  return result

})
</script>
